<template>
  <div class="main">
    <library-topbar @burger-clicked="menuClicked = !menuClicked" :menu-is-open="menuClicked" :is-on-tablet="responsive !== 'desktop'" :connection="connection" :book-archive="bookArchive" />
    <burger
      :is-opened="menuClicked"
      @menu-toggle="menuClicked = $event.value"
      :is-on-tablet="responsive !== 'desktop'"></burger>
    <div class="c-library__container page-wrap">
      <section class="section has-text-white" v-if="books" @click="menuClicked = false">
        <offline-notification v-if="!connection"></offline-notification>
        <div class="c-library__shelf-wrapper c-library__shelf-wrapper--gradient" v-for="(group, i) in articleGroups" :key="i">
          <div class="c-library__header-holder" :class="{'c-library__header-holder--fixed': isLibraryHeaderFixed}" ref="libraryHeader">
            <div v-if="i === 0" class="c-library__wrapper c-library__header">
              <h1 class="c-library__heading">{{ $t('MY_LIBRARY') }}</h1>
              <library-filter v-if="showLibraryFilter" :connection="connection" :filter="filterOffline" @change-filter="setLibraryFilterOffline"></library-filter>
              <div class="c-library__sorting-wrapper">
                <span class="c-library__sorting-text">
                  {{ $t('SORT_BY') }}:
                </span>
                <library-select></library-select>
              </div>
            </div>
          </div>

          <div v-if="i === 0" v-show="isLibraryHeaderFixed" class="c-library__wrapper c-library__header-placeholder">
            Library placeholder
          </div>

          <ul class="c-library__shelf-row">
            <li class="c-library__shelf-item book-item" v-for="(value, index) in lazyBooks.slice(i * numberOfColumns, (i + 1) * numberOfColumns)" v-bind:key="value.id">
              <bubble v-if="!index && !i"></bubble>
              <library-book :book="value" :availableOffline="value.availableOffline" :savedBooks="savedBooks" :getSavedBooks="getSavedBooks" :bookArchive="bookArchive" />
            </li>
          </ul>
        </div>
      </section>
    </div>
    <feedback v-if="true === false" @open-feedback="showFeedback = true" />
    <feedback-box v-if="showFeedback" @close-feedback="showFeedback = false" />
  </div>
</template>

<script>
import {mapState, mapActions} from 'vuex'

import { setDocumentTitle } from '@/utils/utils'

import LibrarySelect from '@/components/LibrarySelect.vue'
import LibraryTopBar from '@/components/LibraryTopBar'
import LibraryFilter from '@/components/LibraryFilter.vue'
import BurgerMenu from '@/components/BurgerMenu'
import LibraryBook from '@/components/LibraryBook'
import OfflineNotification from '@/components/OfflineNotification'
import BookArchiver from '@/services/bookArchiver'
import Bubble from '@/components/Bubble'
import Feedback from '@/components/Feedback'
import FeedbackBox from '@/components/FeedbackBox'

export default {
  name: 'library',

  components: {
    'library-topbar': LibraryTopBar,
    'library-book': LibraryBook,
    'library-filter': LibraryFilter,
    'burger': BurgerMenu,
    'offline-notification': OfflineNotification,
    'library-select': LibrarySelect,
    'bubble': Bubble,
    'feedback': Feedback,
    'feedback-box': FeedbackBox
  },

  data () {
    return {
      menuClicked: false,
      limit: 60,
      bookArchive: {
        selectedBooks: [],
        currentStatus: ''
      },
      isLibraryHeaderFixed: false,
      showFeedback: false
    }
  },

  watch: {
    ['$i18n.locale']() {
      setDocumentTitle(this.$t('LIBRARY'))
    }
  },

  computed: {
    lazyBooks () {
      return this.filteredBooks.slice(0, this.load + this.limit)
    },

    showLibraryFilter() {
      return this.responsive === 'desktop' && this.savedBooks.length > 0
    },

    articleGroups () {
      return Array.from(Array(Math.ceil(this.lazyBooks.length / this.numberOfColumns)).keys())
    },

    filteredBooks () {
      let availableBooks

      this.books.map(book => {
        if (this.savedBooks.indexOf(book.id) > -1) {
          book.availableOffline = true

          return book
        }
        book.availableOffline = false

        return book
      })
      availableBooks = this.connection ? this.books : this.books.filter(book => this.savedBooks.indexOf(book.id) > -1)
      if (this.showLibraryFilter && this.filterOffline === 'downloaded') {
        availableBooks = this.books.filter(book => this.savedBooks.indexOf(book.id) > -1)
      }
      if (this.responsive === 'tablet') {
        if (this.bookArchive.currentStatus !== 'READY_TO_ARCHIVE') {
          availableBooks = this.books.filter(book => this.savedBooks.indexOf(book.id) > -1)
        }
      }
      if (this.filterBy === 'by_newest') {
        return [...availableBooks].sort((a, b) => new Date(b.date) - new Date(a.date))
      } else if (this.filterBy === 'by_title_asc') {
        return [...availableBooks].sort((a, b) => a.title.localeCompare(b.title, 'nb-NO'))
      } else if (this.filterBy === 'by_title_desc') {
        return [...availableBooks].sort((a, b) => b.title.localeCompare(a.title, 'nb-NO'))
      } else if (this.filterBy === 'by_size_asc') {
        return [...availableBooks].sort((a, b) => a.size - b.size)
      } else if (this.filterBy === 'by_size_desc') {
        return [...availableBooks].sort((a, b) => b.size - a.size)
      } else {
        return availableBooks
      }
    },

    numberOfColumns() {
      if (this.responsive === 'mobile') {
        return 2
      } else if (this.responsive === 'tablet') {
        return 3
      } else {
        return 4
      }
    },

    ...mapState('book', {
      books: state => state.books,
      savedBooks: state => state.savedBooks
    }),

    ...mapState('userSettings', {
      connection: state => state.connection,
      filterBy: state => state.libraryFilter.value,
      load: state => state.libraryLoaded,
      libraryScrollPos: state => state.libraryScrollPos,
      filterOffline: state => state.libraryFilterOffline,
      responsive: state => state.responsive
    })
  },

  async created () {
    setDocumentTitle(this.$t('LIBRARY'))
    await this.getSavedBooks()
    await this.fetchBooks()
  },

  async mounted () {
    this.bookArchive = new BookArchiver()

    const checkItem = () => {
      if (this.filteredBooks && document.querySelector('.book-item')) {
        if (this.filteredBooks.length > this.limit && document.querySelectorAll('.book-item').length === this.limit) {
          this.setLibraryLoaded(this.load + this.limit)
        }
      } else {
        clearInterval(untilItemRender)
      }
    }

    const untilItemRender = setInterval(checkItem, 1000)

    window.addEventListener('scroll', this.scroll)
    window.scrollTo(0, this.libraryScrollPos)
  },

  beforeDestroy () {
    window.removeEventListener('scroll', this.scroll)
  },

  methods: {
    scroll () {
      const bottomOfWindow = Math.ceil(window.innerHeight + window.pageYOffset) >= document.body.offsetHeight
      const scrollPos = window.scrollY || document.body.scrollTop || document.documentElement.scrollTop

      if (bottomOfWindow) {
        this.setLibraryLoaded(this.load + this.limit)
      }
      this.setLibraryScrollPos(scrollPos)
      this.stickyLibraryHeader()
    },

    stickyLibraryHeader() {
      const crossBrowserScrollTop = (document.documentElement && document.documentElement.scrollTop) || document.body.scrollTop

      if (this.$refs.libraryHeader && crossBrowserScrollTop > (this.$refs.libraryHeader[0].offsetTop - 50)) {
        this.isLibraryHeaderFixed = true
      } else {
        this.isLibraryHeaderFixed = false
      }
    },

    ...mapActions({
      fetchBooks: 'book/fetchBooks',
      getSavedBooks: 'book/getSavedBooks',
      setLibraryLoaded: 'userSettings/setLibraryLoaded',
      setLibraryScrollPos: 'userSettings/setLibraryScrollPos',
      setLibraryFilterOffline: 'userSettings/setLibraryFilterOffline'
    })
  }
}
</script>

<style lang="scss">
@import '@/assets/scss/_vars.scss';
@import '@/assets/scss/_breakpoints.scss';

html {
  background: linear-gradient(to top, $white 0%, $dark-blue 500%);
  min-height: 100vh;
}

.c-library__header-placeholder {
  font-size: 1.3em;
  color: transparent;
}

.c-library__wrapper {
  margin: 0 auto;
  padding: 50px 20px 50px !important;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.c-library__header-holder {
  z-index: 998;

  &--fixed {
    position: fixed;

    background: $grey;
    top: 65px;
    left: 0;
    right: 0;

    > .c-library__header {
      padding: 15px 20px!important;
    }
  }
}

.c-library__heading {
  font-size: 1.3em;
  font-weight: 700;
  color: $dark-blue;
}

.c-library__sorting-text {
  color: $dark-blue;
  font-weight: 600;
}

.section {
  padding: 0 !important;
  background: $white;
}

.c-library__shelf-wrapper {
  padding-top: 5em;
}

.c-library__shelf-wrapper--gradient {
  background: linear-gradient(to bottom, $white 0%, $dark-blue 500%);
}

.c-library__shelf-row {
  display: flex;
  margin: 0 auto;
  width: 100%;

  > .c-library__shelf-item {
    flex: 0 1 auto;
  }
}

.c-library__shelf-item {
  width: 50%;
  margin-bottom: 4em;
  position: relative;
}

@include tablet {
  .c-library__wrapper {
    max-width: 36em;
    width: 100%;
  }

  .c-library__shelf-item {
    width: 33%;
  }

  .c-library__shelf-row {
    max-width: 36em;
    width: 100%;
  }
}

@include desktop {
  .c-library__wrapper {
    max-width: 60em;
    width: 100%;
  }

  .c-library__shelf-item {
    width: 25%;
  }

  .c-library__shelf-row {
    max-width: 60em;
    width: 100%;
  }
}
</style>
